import { ApiMessageStack, SelectInputOptionsController } from "components";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Box, useToast } from "@chakra-ui/react";
import { DEFAULT_ERROR_TOAST_OPTIONS } from "constants/default-toast-options";
import { AccountLeadsStore } from "store/UserAccounts/UserAccount/AccountLeads/AccountLeads.store";
import { observer } from "mobx-react";
import useSelect from "utils/react-hooks/useSelect.hook";
import { AccountOfficesStore } from "store/UserAccounts/UserAccount/AccountOffices/AccountOffices.store";
import { LeadOfficeSelectOptionCondensed } from "components/select-input/custom-options/LeadOfficeSelectOptions/LeadOfficeSelectOptionCondensed";

interface IProps {
  leadsStore: AccountLeadsStore;
  accountOfficesStore: AccountOfficesStore;
  selectedRowIds: number[];
  setIsSubmitting: (isSubmitting: boolean) => void;
}

export const BulkUpdateOfficeMenu: FC<IProps> = observer(
  ({ leadsStore, selectedRowIds, setIsSubmitting, accountOfficesStore }) => {
    const toast = useToast();
    const [office, setOffice] = useState<number | null>(null);
    const officeRef = useRef<number | null>(null);
    const officeOptions = accountOfficesStore.accountOfficesOptionsArray;

    const handleChange = useCallback((newOffice: number) => {
      setOffice(newOffice);
      officeRef.current = newOffice;
    }, []);

    const { visibleOptions, getOptionProps, selectedOption } = useSelect({
      multi: false,
      options: officeOptions,
      value: office,
      onChange: handleChange,
    });

    useEffect(() => {
      const cleanup = async () => {
        if (officeRef.current) {
          try {
            setIsSubmitting(true);
            await leadsStore.updateLeads(selectedRowIds, {
              officeId: officeRef.current,
            });
          } catch (error) {
            toast({
              ...DEFAULT_ERROR_TOAST_OPTIONS,
              description: <ApiMessageStack messageStack={error.message} />,
            });
          } finally {
            setIsSubmitting(false);
          }
        }
      };

      return () => {
        cleanup();
      };
    }, [leadsStore, selectedRowIds, toast, setIsSubmitting]);

    return (
      <Box
        maxHeight={"320px"}
        flexGrow={1}
        cursor={"pointer"}
        color={"gray.800"}
        overflowY={"auto"}
      >
        <SelectInputOptionsController<number>
          selectedOption={selectedOption}
          visibleOptions={visibleOptions}
          getOptionProps={getOptionProps}
          optionComponent={optionProps => (
            <LeadOfficeSelectOptionCondensed optionProps={optionProps} />
          )}
        />
      </Box>
    );
  }
);
